import { Validator } from '../validator/Validator';
import {ValidationError, ValidationResult, ValidationRuleConfig, ValidatorOptions} from '../types';

export type ValidationPipe = (data: Record<string, any>) => ValidationResult;

export function createPipe(
    rules: Record<string, ValidationRuleConfig[]>,
    options?: ValidatorOptions
): ValidationPipe {
    return (data: Record<string, any>) => {
        const validator = new Validator(data, options);
        return validator.validateRules(rules);
    };
}

export function createTemplatePipe(
    template: any[],
    options?: ValidatorOptions
): ValidationPipe {
    return (data: Record<string, any>) => {
        const validator = new Validator(data, options);
        return validator.validateTemplate(template);
    };
}

export function pipe(
    ...pipes: ValidationPipe[]
): ValidationPipe {
    return (data: Record<string, any>) => {
        let result: ValidationResult = {
            isValid: true,
            errors: [],
            data
        };

        for (const pipeFn of pipes) {
            const pipeResult = pipeFn(data);

            if (!pipeResult.isValid) {
                result.isValid = false;
                result.errors.push(...pipeResult.errors);
            }

            result.data = { ...result.data, ...pipeResult.data };
        }

        return result;
    };
}

export function mapErrors(
    pipe: ValidationPipe,
    mapper: (errors: ValidationError[]) => ValidationError[]
): ValidationPipe {
    return (data: Record<string, any>) => {
        const result = pipe(data);
        return {
            ...result,
            errors: mapper(result.errors)
        };
    };
}

export function transformData(
    pipe: ValidationPipe,
    transformer: (data: Record<string, any>) => Record<string, any>
): ValidationPipe {
    return (data: Record<string, any>) => {
        const transformedData = transformer(data);
        const result = pipe(transformedData);
        return {
            ...result,
            data: transformedData
        };
    };
}

export function conditional(
    condition: (data: Record<string, any>) => boolean,
    truePipe: ValidationPipe,
    falsePipe?: ValidationPipe
): ValidationPipe {
    return (data: Record<string, any>) => {
        if (condition(data)) {
            return truePipe(data);
        } else if (falsePipe) {
            return falsePipe(data);
        }

        return {
            isValid: true,
            errors: [],
            data
        };
    };
}

export function tap(
    pipe: ValidationPipe,
    callback: (result: ValidationResult) => void
): ValidationPipe {
    return (data: Record<string, any>) => {
        const result = pipe(data);
        callback(result);
        return result;
    };
}

export function catchError(
    pipe: ValidationPipe,
    handler: (error: Error, data: Record<string, any>) => ValidationResult
): ValidationPipe {
    return (data: Record<string, any>) => {
        try {
            return pipe(data);
        } catch (error) {
            return handler(error as Error, data);
        }
    };
}

export function withDefault(
    pipe: ValidationPipe,
    defaultData: Record<string, any>
): ValidationPipe {
    return (data: Record<string, any>) => {
        const mergedData = { ...defaultData, ...data };
        const result = pipe(mergedData);
        return {
            ...result,
            data: mergedData
        };
    };
}

export function validateField(
    field: string,
    rules: ValidationRuleConfig[],
    options?: ValidatorOptions
): ValidationPipe {
    return (data: Record<string, any>) => {
        const validator = new Validator(data, options);
        const result = validator.validateRules({ [field]: rules });

        if (result.isValid) {
            return {
                isValid: true,
                errors: [],
                data: { [field]: data[field] }
            };
        }

        return result;
    };
}

export function mergePipes(
    ...pipes: ValidationPipe[]
): ValidationPipe {
    return (data: Record<string, any>) => {
        const results = pipes.map(pipe => pipe(data));

        const isValid = results.every(result => result.isValid);
        const errors = results.flatMap(result => result.errors);
        const mergedData = results.reduce((acc, result) => ({ ...acc, ...result.data }), {});

        return {
            isValid,
            errors,
            data: mergedData
        };
    };
}